import * as XLSX from "xlsx";
import { buildPreviewFromMapping, guessColumnMapping } from "./parse-spreadsheet";

export const SAMPLE_FILE_NAME = "routebot-sample-roster.xlsx";

export const SAMPLE_SCHOOLS = [
  {
    id: "riverside",
    name: "Riverside Academy",
    address: "400 Congress Avenue, Austin, TX",
    lat: 30.2672,
    lng: -97.7431,
    color: "#5eead4",
  },
  {
    id: "oakridge",
    name: "Oakridge Elementary",
    address: "1800 West 35th Street, Austin, TX",
    lat: 30.3074,
    lng: -97.7518,
    color: "#93c5fd",
  },
  {
    id: "summit",
    name: "Summit High School",
    address: "5401 McCarty Lane, Austin, TX",
    lat: 30.2214,
    lng: -97.8372,
    color: "#fcd34d",
  },
];

export const SAMPLE_DRIVERS = [
  { id: "d1", name: "Marcus Hale", routeIds: ["r-riverside-1"], vehicle: "Bus 12" },
  { id: "d2", name: "Priya Shah", routeIds: ["r-riverside-2"], vehicle: "Bus 18" },
  { id: "d3", name: "Elena Ruiz", routeIds: ["r-oakridge-1"], vehicle: "Van 4" },
  { id: "d4", name: "Jonah Blake", routeIds: ["r-oakridge-2"], vehicle: "Bus 7" },
  { id: "d5", name: "Amira Cole", routeIds: ["r-summit-1"], vehicle: "Bus 21" },
  { id: "d6", name: "Theo Park", routeIds: ["r-summit-2"], vehicle: "Bus 22" },
];

const FIRST_NAMES = [
  "Ava",
  "Liam",
  "Noah",
  "Mia",
  "Sophia",
  "Lucas",
  "Amelia",
  "Ethan",
  "Harper",
  "Mason",
  "Isla",
  "Leo",
  "Chloe",
  "Henry",
  "Zoe",
  "Owen",
  "Lily",
  "Jack",
  "Nora",
  "Caleb",
  "Ella",
  "Levi",
  "Aria",
  "Grace",
  "Ezra",
  "Ruby",
  "Miles",
  "Ivy",
  "Felix",
  "Luna",
];

const LAST_NAMES = [
  "Nguyen",
  "Patel",
  "Brooks",
  "Khan",
  "Reyes",
  "Walsh",
  "Okoye",
  "Bennett",
  "Silva",
  "Huang",
  "Cohen",
  "Diaz",
  "Murray",
  "Ibrahim",
  "Foster",
  "Sato",
  "Quinn",
  "Adeyemi",
  "Keller",
  "Vargas",
];

const STREETS = [
  "Willow Avenue",
  "Cedar Lane",
  "Maple Court",
  "Ridge Road",
  "Harbor Street",
  "Elmwood Drive",
  "Sunset Boulevard",
  "Oak Hollow",
  "Pearl Street",
  "Canyon View",
];

const PARENT_FIRST = ["Jordan", "Sam", "Alex", "Casey", "Riley", "Morgan", "Taylor", "Jamie", "Cameron", "Drew"];

const mulberry32 = (seed) => {
  let a = seed;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const schoolForIndex = (index) => {
  if (index < 30) return SAMPLE_SCHOOLS[0];
  if (index < 58) return SAMPLE_SCHOOLS[1];
  return SAMPLE_SCHOOLS[2];
};

const offsetAround = (school, rand) => {
  const lat = school.lat + (rand() - 0.5) * 0.06;
  const lng = school.lng + (rand() - 0.5) * 0.08;
  return { lat: Number(lat.toFixed(5)), lng: Number(lng.toFixed(5)) };
};

// The sample is a complete, clean roster: every row has an address with its coordinates and a
// parent phone, so a workspace built from it has all 85 students on the map. The coordinates travel
// with the rows into the import, which places them exactly instead of geocoding invented streets.
export const SAMPLE_STUDENT_COUNT = 85;

export const buildSampleStudents = () => {
  const rand = mulberry32(85);
  return Array.from({ length: SAMPLE_STUDENT_COUNT }, (_, index) => {
    const school = schoolForIndex(index);
    const first = FIRST_NAMES[index % FIRST_NAMES.length];
    const last = LAST_NAMES[index % LAST_NAMES.length];
    const name = `${first} ${last}`;
    const streetNo = 100 + ((index * 17) % 700);
    const street = STREETS[index % STREETS.length];
    const geo = offsetAround(school, rand);
    const parent = `${PARENT_FIRST[index % PARENT_FIRST.length]} ${last}`;
    return {
      id: `sample-student-${index + 1}`,
      name,
      school: school.name,
      schoolId: school.id,
      address: `${streetNo} ${street}, Austin, TX`,
      parent,
      phone: `+1 512 555 ${String(1000 + index).slice(-4)}`,
      email: `${first.toLowerCase()}.${last.toLowerCase()}@example.com`,
      grade: school.id === "summit" ? String(9 + (index % 4)) : String(1 + (index % 5)),
      lat: geo.lat,
      lng: geo.lng,
      missing: [],
      recognized: true,
      rowNumber: index + 2,
    };
  });
};

// The sample as the import understands it: the same shape /analyze produces for an upload, with
// schools (addresses included) and per-row coordinates. Deterministic, so a restored journey can rebuild it.
export const getSampleExtraction = () => {
  const students = buildSampleStudents();
  return {
    sheetKind: "school_transport_roster",
    interpreter: "sample",
    schools: SAMPLE_SCHOOLS.map((school) => ({
      name: school.name,
      address: school.address,
      lat: school.lat,
      lng: school.lng,
    })),
    routes: [],
    warnings: [],
    passengers: students.map((student) => ({
      rowNumber: student.rowNumber,
      name: student.name,
      schoolName: student.school,
      schoolAddress: SAMPLE_SCHOOLS.find((school) => school.id === student.schoolId)?.address || "",
      addressGetOn: student.address,
      lat: student.lat,
      lng: student.lng,
      grade: student.grade,
      email: student.email,
      parents: [{ name: student.parent, phone: student.phone }],
    })),
  };
};

const chunk = (items, size) => {
  const groups = [];
  for (let i = 0; i < items.length; i += size) groups.push(items.slice(i, i + size));
  return groups;
};

export const buildSampleRoutes = (students = buildSampleStudents()) => {
  const bySchool = SAMPLE_SCHOOLS.map((school) => ({
    school,
    students: students.filter((student) => student.schoolId === school.id && student.lat != null),
  }));

  const routes = [];
  const capacities = { riverside: 15, oakridge: 14, summit: 13 };

  bySchool.forEach(({ school, students: schoolStudents }) => {
    chunk(schoolStudents, capacities[school.id]).forEach((group, index) => {
      const id = `r-${school.id}-${index + 1}`;
      const driver = SAMPLE_DRIVERS.find((item) => item.routeIds.includes(id)) || SAMPLE_DRIVERS[routes.length % SAMPLE_DRIVERS.length];
      routes.push({
        id,
        name: `${school.name.split(" ")[0]} AM ${index + 1}`,
        schoolId: school.id,
        schoolName: school.name,
        color: school.color,
        driverId: driver.id,
        driverName: driver.name,
        vehicle: driver.vehicle,
        studentIds: group.map((student) => student.id),
        students: group,
        stops: [
          { kind: "start", label: `${driver.vehicle} depot`, lat: school.lat + 0.012, lng: school.lng - 0.01 },
          ...group.map((student) => ({
            kind: "student",
            label: student.name,
            address: student.address,
            lat: student.lat,
            lng: student.lng,
          })),
          { kind: "school", label: school.name, lat: school.lat, lng: school.lng },
        ],
      });
    });
  });

  return routes;
};

export const buildSampleOperation = () => {
  const students = buildSampleStudents();
  const routes = buildSampleRoutes(students);
  return {
    illustrative: true,
    label: "Demonstration routes from the sample roster",
    schools: SAMPLE_SCHOOLS,
    students,
    routes,
    drivers: SAMPLE_DRIVERS,
    stats: {
      studentCount: students.length,
      schoolCount: SAMPLE_SCHOOLS.length,
      routeCount: routes.length,
      driverCount: SAMPLE_DRIVERS.length,
      missingAddressCount: students.filter((s) => s.missing.includes("address")).length,
    },
  };
};

export const SAMPLE_HEADERS = ["Student Name", "School", "Address", "Parent Name", "Parent Phone", "Grade"];

export const getSampleSpreadsheetRows = (students = buildSampleStudents()) => {
  return students.map((student) => [
    student.name,
    student.school,
    student.address,
    student.parent,
    student.phone,
    student.grade,
  ]);
};

export const getSampleParsedSpreadsheet = () => {
  const students = buildSampleStudents();
  const headers = SAMPLE_HEADERS;
  const rows = getSampleSpreadsheetRows(students);
  const mapping = guessColumnMapping(headers);
  const preview = buildPreviewFromMapping(rows, mapping, headers);
  preview.students = preview.students.map((row, index) => ({
    ...row,
    id: students[index].id,
    schoolId: students[index].schoolId,
    lat: students[index].lat,
    lng: students[index].lng,
  }));
  return {
    sheetName: "Students",
    headers,
    rows,
    mapping,
    headerInferred: false,
    stats: {
      rowCount: rows.length,
      schoolCount: SAMPLE_SCHOOLS.length,
      recognizedCount: preview.recognizedCount,
      missingAddressCount: preview.missingAddressCount,
      mappedFields: ["name", "school", "address", "parent", "phone", "grade"],
    },
    preview,
  };
};

export const buildSampleWorkbookBuffer = () => {
  const workbook = XLSX.utils.book_new();
  const rows = [SAMPLE_HEADERS, ...getSampleSpreadsheetRows()];
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, sheet, "Students");
  return XLSX.write(workbook, { type: "array", bookType: "xlsx" });
};

export const downloadSampleSpreadsheet = () => {
  const buffer = buildSampleWorkbookBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = SAMPLE_FILE_NAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const attachSampleOperationToPreview = (preview) => {
  const operation = buildSampleOperation();
  return {
    ...operation,
    students: operation.students,
    preview,
  };
};
